import React, { useState } from 'react';
import { Copy, CheckCheck, ChevronDown } from 'lucide-react';

function CopyBlock({ label, text }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">{label}</p>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg bg-white dark:bg-gray-600 border border-gray-200 dark:border-gray-500 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-500 transition-colors"
        >
          {copied ? <><CheckCheck className="w-3 h-3 text-green-500" /> 복사됨</> : <><Copy className="w-3 h-3" /> 복사</>}
        </button>
      </div>
      <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line leading-relaxed">{text}</p>
    </div>
  );
}

function Section({ title, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left"
      >
        <span className="font-semibold text-gray-900 dark:text-white text-sm">{title}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-gray-100 dark:border-gray-700 pt-4 space-y-3">
          {children}
        </div>
      )}
    </div>
  );
}

export default function AdminSalesGuide() {
  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div>
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">영업 가이드</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">복사 버튼으로 바로 카카오톡/문자에 붙여넣기 하세요.</p>
      </div>

      <Section title="① 체험 신청 후 — 첫 연락 메시지">
        <CopyBlock
          label="카카오톡 / 문자"
          text={`안녕하세요 대표님, GYM Manager입니다 😊
무료 체험 신청 주셔서 감사합니다!

14일간 모든 기능을 무제한으로 사용해보실 수 있습니다.
아래 링크로 접속하시면 바로 시작하실 수 있어요.

[초대링크]

이용 중 궁금하신 점은 언제든지 연락 주세요!`}
        />
      </Section>

      <Section title="② 체험 중 — 중간 점검 메시지 (체험 7일차)">
        <CopyBlock
          label="카카오톡 / 문자"
          text={`안녕하세요 대표님 😊
GYM Manager 체험 시작하신 지 일주일이 됐네요!

잘 활용하고 계신가요?
혹시 어렵거나 궁금하신 부분 있으시면 편하게 말씀해 주세요.

남은 체험 기간 동안 최대한 도움 드리겠습니다!`}
        />
      </Section>

      <Section title="③ 체험 종료 전 — 전환 유도 메시지 (체험 12일차)">
        <CopyBlock
          label="카카오톡 / 문자"
          text={`안녕하세요 대표님 😊
무료 체험이 곧 종료됩니다.

✅ GYM Manager 이용권 안내
• 월 정기권: 49,000원/월
• 연간 결제: 490,000원 (2개월 무료 혜택)

💳 입금 계좌: 국민은행 703001-01-598295 (티앤티 주식회사)

입금 후 앱 설정 페이지에서 납부 확인 요청을 해주시면
바로 이용권이 연장됩니다.

궁금하신 점 있으시면 편하게 연락 주세요!`}
        />
      </Section>

      <Section title="④ 계약 실패 후 — 재연락 메시지 (1개월 후)">
        <CopyBlock
          label="카카오톡 / 문자"
          text={`안녕하세요 대표님 😊
GYM Manager입니다.

지난번에 체험 이용해 주셨는데, 잘 지내고 계신가요?

저희 서비스가 업데이트 되어 회원 앱 푸시 알림 기능이 추가됐습니다.
(회원권 만료 알림, PT 수업 전날 알림 등 자동 발송)

다시 한번 사용해 보실 의향이 있으시면 말씀해 주세요.
14일 체험 다시 드릴 수 있습니다 😊`}
        />
      </Section>

      <Section title="⑤ 요금 문의 시 — 안내 메시지">
        <CopyBlock
          label="카카오톡 / 문자"
          text={`안녕하세요 대표님 😊
GYM Manager 요금 안내 드립니다.

✅ 이용권 종류
• 월 정기권: 49,000원/월
• 연간 결제: 490,000원 (2개월 무료 혜택)

✅ 포함 기능 (회원 수 무제한)
• 회원 관리 + QR 출석 체크
• PT 관리 + 트레이너 포털
• 회원 전용 앱 (회원권 확인, PT 일정, QR)
• 푸시 알림 (회원권 만료, PT 전날 알림 자동 발송)
• 매출 통계 분석

💳 입금 계좌: 국민은행 703001-01-598295 (티앤티 주식회사)
예금주: 티앤티 주식회사

추가 문의 사항 있으시면 편하게 연락 주세요!`}
        />
      </Section>

      <Section title="⑥ 기타 — 고객센터 안내">
        <CopyBlock
          label="고객센터 정보"
          text={`고객센터: 1877-1827
운영시간: 평일 09:00 ~ 18:00
이메일: ezbiz.manager@gmail.com`}
        />
      </Section>
    </div>
  );
}
